import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, Menu, ShoppingBag, Receipt, Check, Bell } from 'lucide-react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

const PayBillPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const restaurantId = searchParams.get('r');
  const tableId = searchParams.get('t');
  const customerId = searchParams.get('c'); // Get customer ID from URL params
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [table, setTable] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [requestingBill, setRequestingBill] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Handle swipe gestures for page navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    setTouchEnd(e.changedTouches[0].clientX);
  };

  // Detect swipe and navigate
  useEffect(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      // Swipe left - nothing, Pay Bill is the last page
    } else if (isRightSwipe) {
      // Swipe right - go back to Orders
      navigate(`/orders?r=${restaurantId}&t=${tableId}`);
    }
  }, [touchStart, touchEnd, navigate, restaurantId, tableId]);

  useEffect(() => {
    if (!restaurantId || !tableId) {
      toast.error('Invalid restaurant or table');
      navigate('/');
      return;
    }
    fetchOrders();
    fetchRestaurantInfo();
    fetchTableInfo();
  }, [restaurantId, tableId]);

  const fetchRestaurantInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/public-info`);
      setRestaurant(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurant info:', error);
    }
  };

  const fetchTableInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/tables/${tableId}`);
      setTable(response.data);
    } catch (error) {
      console.error('Failed to fetch table info:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/qr-orders/table/${tableId}/all`);
      const allOrders = response.data || [];
      
      // Filter only UNPAID orders (current running orders) and exclude CANCELLED
      const unpaidOrders = allOrders.filter(order => 
        order.orderStatus !== 'CANCELLED' && (order.paymentStatus === 'UNPAID' || order.orderStatus === 'PLACED')
      );
      
      console.log('📊 Total orders:', allOrders.length, 'Unpaid orders:', unpaidOrders.length);
      setOrders(unpaidOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = orders.reduce((sum, order) => sum + parseFloat(order.grandTotal || 0), 0);
  const cgst = totalAmount * 0.025; // 2.5%
  const sgst = totalAmount * 0.025; // 2.5%
  const grandTotal = totalAmount + cgst + sgst;

  const handlePayment = async () => {
    if (orders.length === 0) {
      toast.error('No orders to pay for');
      return;
    }

    try {
      toast.loading('Processing payment...');
      // This endpoint needs to be created in backend
      await axios.post(`${API_URL}/api/restaurants/${restaurantId}/payments`, {
        tableId,
        orderIds: orders.map(o => o.id),
        totalAmount: grandTotal,
        paymentMethod
      });
      
      toast.dismiss();
      toast.success('Payment successful! Thank you for dining with us.');
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.dismiss();
      toast.error('Payment failed. Please try again or contact staff.');
      console.error('Payment error:', error);
    }
  };

  const handleRequestBill = async () => {
    setRequestingBill(true);
    toast.loading('📋 Preparing bill request...', { id: 'bill-request' });
    try {
      // Get customerId from URL params or first order
      const customerIdToUse = customerId || orders[0]?.customerId;
      
      // Always fetch latest table orders to get current running orders
      let orderData = [];
      let totalAmount = 0;
      let itemsCount = 0;
      let orderNumbers = '';
      let mainOrderId = null;
      let mainCustomerId = null;
      
      try {
        toast.loading('📊 Fetching order details...', { id: 'bill-request' });
        // Fetch all orders for this table
        const allOrdersResponse = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/qr-orders/table/${tableId}/all`);
        const allOrders = allOrdersResponse.data || [];
        
        // Filter only UNPAID orders (current running orders) and exclude CANCELLED
        const unpaidOrders = allOrders.filter(order => 
          order.orderStatus !== 'CANCELLED' && (order.paymentStatus === 'UNPAID' || order.orderStatus === 'PLACED')
        );
        
        if (unpaidOrders.length > 0) {
          orderData = unpaidOrders;
          
          console.log('📦 Unpaid Orders Raw Data:', unpaidOrders);
          
          // Calculate totals from unpaid orders
          totalAmount = unpaidOrders.reduce((sum, order) => {
            const amount = order.grandTotal || order.totalAmount || 0;
            console.log(`  Order ${order.orderNumber}: grandTotal=${order.grandTotal}, totalAmount=${order.totalAmount}, using=${amount}`);
            return sum + parseFloat(amount);
          }, 0);
          
          // Count items from unpaid orders
          itemsCount = unpaidOrders.reduce((total, order) => {
            return total + (order.items?.length || 0);
          }, 0);
          
          // Get order numbers (try different field names)
          orderNumbers = unpaidOrders.map(order => 
            order.orderNumber || order.order_number || `ORD-${order.orderId || order.order_id}`
          ).join(', ');
          
          // Use the latest/main order details
          const mainOrder = unpaidOrders[0];
          mainOrderId = mainOrder.orderId || mainOrder.order_id;
          mainCustomerId = mainOrder.customerId || mainOrder.customer_id;
          
          console.log('📋 Unpaid Orders Found:', unpaidOrders.length);
          console.log('💰 Calculated Total Amount:', totalAmount);
          console.log('📦 Items Count:', itemsCount);
          console.log('🔢 Order Numbers:', orderNumbers);
        } else {
          // No unpaid orders found, use current page data as fallback
          orderData = orders;
          totalAmount = grandTotal || 0;
          itemsCount = orders.reduce((total, order) => total + (order.items?.length || 0), 0);
          orderNumbers = orders.map(o => o.orderNumber || `ORD-${o.orderId}`).join(', ');
          mainOrderId = orders[0]?.orderId;
          mainCustomerId = customerIdToUse;
        }
      } catch (error) {
        console.log('Could not fetch table orders, using current page data');
        // Fallback to current page data
        orderData = orders;
        totalAmount = grandTotal || 0;
        itemsCount = orders.reduce((total, order) => total + (order.items?.length || 0), 0);
        orderNumbers = orders.map(o => o.orderNumber || `ORD-${o.orderId}`).join(', ');
        mainOrderId = orders[0]?.orderId;
        mainCustomerId = customerIdToUse;
      }
      
      // ❌ Check if order has any items - don't send request for empty orders
      if (!itemsCount || itemsCount === 0) {
        toast.dismiss('bill-request');
        toast.error('❌ Cannot request bill - No items in order! Please add items first.', {
          duration: 5000
        });
        setRequestingBill(false);
        return;
      }
      
      // ❌ Check if order has any amount - don't send request for zero amount
      if (!totalAmount || totalAmount === 0) {
        toast.dismiss('bill-request');
        toast.error('❌ Cannot request bill - Order total is zero! Please add items first.', {
          duration: 5000
        });
        setRequestingBill(false);
        return;
      }
      
      // Send bill request with proper data (backend will handle duplicates intelligently)
      toast.loading('📤 Sending bill request to counter...', { id: 'bill-request' });
      const billRequestResponse = await axios.post(`${API_URL}/api/billing-pos/desktop/restaurants/${restaurantId}/bill-request`, {
        tableId,
        customerId: mainCustomerId || customerIdToUse,
        orderId: mainOrderId,
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        itemsCount,
        tableName: table?.tableName || `Table ${table?.tableNumber || tableId}`,
        orderNumbers: orderNumbers || 'No orders'
      });
      
      console.log('✅ Bill Request Response:', billRequestResponse.data);
      
      // Handle different response types
      toast.dismiss('bill-request');
      if (billRequestResponse.data.success) {
        if (billRequestResponse.data.updated) {
          toast.success('✅ Bill request updated! (New items added - Amount: ₹' + totalAmount.toFixed(2) + ')', {
            duration: 5000,
            icon: '🔄'
          });
        } else {
          toast.success('✅ Bill request sent to counter! Staff will bring your bill shortly.', {
            duration: 5000,
            icon: '🔔'
          });
        }
      } else if (billRequestResponse.data.duplicate) {
        toast('⏳ Bill request already sent! Please wait for staff.', {
          icon: '⏳',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Failed to request bill:', error);
      toast.dismiss('bill-request');
      
      // Handle backend validation errors
      if (error.response?.status === 400 && error.response?.data?.duplicate) {
        toast('⏳ Bill request already sent for this order! Please wait for staff.', {
          icon: '⏳',
          duration: 4000
        });
      } else {
        toast.error('❌ Failed to send bill request. Please try again.', {
          duration: 5000
        });
      }
    } finally {
      setRequestingBill(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gray-50"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Toast notifications */}
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            fontSize: '14px',
            padding: '12px 16px',
          },
          success: {
            duration: 5000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 shadow-lg sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3">
          <p className="mb-1 text-center text-[10px] sm:text-xs font-bold text-orange-100/95 tracking-wide">
            Powered by Trivana Hospitality
          </p>
          <div className="flex items-center justify-start">
            <div>
              <h1 className="text-xl font-bold text-white">
                {restaurant?.name || 'Restaurant'}
              </h1>
              <p className="text-sm text-orange-100">
                Table {table?.tableNumber || tableId}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bill Details */}
      <div className="max-w-md mx-auto px-4 py-4 pb-20">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Bill Summary</h2>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading bill...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No orders to pay for</p>
            
            {/* Request Bill Button - even when no unpaid orders */}
            <button
              onClick={handleRequestBill}
              disabled={requestingBill}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
            >
              <Bell className="w-5 h-5" />
              {requestingBill ? 'Sending Request...' : 'Request for Bill'}
            </button>
            
            <button
              onClick={() => navigate(`/menu?r=${restaurantId}&t=${tableId}`)}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Browse Menu
            </button>
          </div>
        ) : (
          <>
            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <h3 className="font-semibold text-gray-800 mb-3">Order Details</h3>
              <div className="space-y-3">
                {orders.map((order) => (
                  <div key={order.orderId || order.order_id || order.id}>
                    <p className="text-sm text-gray-500 mb-2">Order #{order.orderNumber}</p>
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                          {item.quantity}x {item.itemName}
                        </span>
                        <span className="text-gray-800 font-medium">
                          ₹{item.totalPrice}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Request Bill Button */}
            <button
              onClick={handleRequestBill}
              disabled={requestingBill}
               className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
            >
              <Bell className="w-5 h-5" />
              {requestingBill ? 'Sending Request...' : 'Request for Bill'}
            </button>
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="max-w-md mx-auto px-3 py-2 flex justify-around items-center">
          <button 
            onClick={() => navigate('/tables')}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </button>
          <button 
            onClick={() => navigate(`/menu?r=${restaurantId}&t=${tableId}`)}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Menu className="w-5 h-5" />
            <span className="text-xs">Menu</span>
          </button>
          <button 
            onClick={() => navigate(`/orders?r=${restaurantId}&t=${tableId}`)}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="text-xs">Orders</span>
          </button>
          <button 
            className="flex flex-col items-center gap-0.5 text-orange-600"
          >
            <Receipt className="w-5 h-5" />
            <span className="text-xs font-medium">Pay Bill</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayBillPage;
